import React, { useState } from "react";
import PageLayout from "@/components/layout/PageLayout";
import TypingAnimation from "@/components/ui/TypingAnimation";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { submitToPabbly } from "@/utils/pabblyConnect";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";

const BusinessWebsiteLanding: React.FC = () => {
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const screenshots = [
    "/lovable-uploads/0ae37432-fcfd-489e-9876-96dbf3fd5d58.png",
    "/lovable-uploads/1515b9b5-1316-4ad0-bc10-ce5100ccf234.png",
    "/lovable-uploads/2060592f-90be-4612-aaee-8a55d9b15dc0.png",
    "/lovable-uploads/235a6b4f-fca6-4062-a954-ffffe27b2194.png",
    "/lovable-uploads/4b2208d6-68d3-4b9f-a21e-f47bd8f115ae.png",
    "/lovable-uploads/5b79d2ca-02cf-4a09-af67-f19ff9916154.png",
    "/lovable-uploads/908e0364-dc0e-4b62-a951-5740a58e2736.png",
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const success = await submitToPabbly(
        {
          firstName,
          email,
          phone,
          source: "facebook-meta-ad",
          page: window.location.pathname,
        },
        "business_website_landing_form"
      );
      if (success && typeof window !== "undefined" && typeof window.fbq === "function") {
        window.fbq("track", "Lead");
      }
      setFirstName("");
      setEmail("");
      setPhone("");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PageLayout
      title="Get a Web Design Quote"
      description="Get a web design quote for your organization. Custom websites that elevate your brand."
      canonicalPath="/business-website"
      ogImage="https://portal.astrem.co/users/u9/9466/astremagency/lovableagentsite/astrem_og_Websites%20Content%20Marketing%20Solutions.jpg"
      navStyle="light"
    >
      {/* Small hero with typewriter at the top */}
      <section className="bg-gradient-to-br from-astrem-gray-900 to-astrem-black pt-24 md:pt-28 pb-8">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <div className="inline-block rounded-full bg-white/10 px-4 py-2">
              <TypingAnimation
                phrases={[
                  "360 Tour Production",
                  "SEO",
                  "Drone work",
                  "Photography",
                  "Website design",
                  "Custom software",
                  "Google Ads Management",
                  "Meta Ads Management",
                ]}
                staticText="that elevates your brand"
                className="text-white text-sm md:text-base"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Main CTA + form */}
      <main className="py-10 md:py-14 bg-white">
        <div className="container mx-auto px-4 max-w-2xl">
          <h1 className="text-3xl md:text-5xl font-display font-semibold text-astrem-black text-center mb-6">
            Get a web design quote for your organization.
          </h1>

          <Card className="border border-astrem-gray-200 shadow-sm">
            <CardContent className="p-6">
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="firstName">First name</Label>
                    <Input
                      id="firstName"
                      name="firstName"
                      placeholder="Jane"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      required
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="jane@company.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="phone">Phone number</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      placeholder="(555) 123-4567"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      required
                    />
                  </div>
                </div>

                <Button type="submit" className="w-full" disabled={isSubmitting}>
                  {isSubmitting ? "Submitting..." : "Get my quote"}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Screenshot slider */}
      <section className="py-12 bg-astrem-gray-50">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="sr-only">Website work samples</h2>
          <div className="relative">
            <Carousel className="w-full" opts={{ align: "start" }}>
              <CarouselContent>
                {screenshots.map((src, i) => (
                  <CarouselItem key={i} className="md:basis-1/2 lg:basis-1/3">
                    <div className="aspect-[16/9] overflow-hidden rounded-lg border border-astrem-gray-200 bg-white">
                      <img
                        src={src}
                        alt={`Astrem website design sample ${i + 1}`}
                        className="h-full w-full object-cover"
                        loading="lazy"
                      />
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious className="hidden md:flex" />
              <CarouselNext className="hidden md:flex" />
            </Carousel>
          </div>
        </div>
      </section>
    </PageLayout>
  );
};

export default BusinessWebsiteLanding;
